import React from 'react';
import { Box, DialogContentText, DialogTitle, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { User, sendEmailVerification } from 'firebase/auth';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import PrimaryButton from '../../shared/button/PrimaryButton';
import GhostButton from '../../shared/button/GhostButton';

/**
 * Handles the initial Email & Password sign in of the login flow.
 * @param {User | null} sign_in_user Currently set global Signed In User.
 * @param {number} verify_timeout Current countdown value for timeout of repeated verifications.
 * @param {(seconds: number): void} returnToSignIn Sets the login state back to initial main Sign In modal.
 * @param {(): void} resetVerifyTimeout Resets the counter for preventing instant clicking of verification steps.
 * @returns {JSX.Element}
 */
const VerifyEmail = ({
  sign_in_user,
  verify_timeout,
  returnToSignIn,
  resetVerifyTimeout
}: {
  sign_in_user: User | null;
  verify_timeout: number;
  returnToSignIn: () => void;
  resetVerifyTimeout: () => void;
}): JSX.Element => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);

  // Handles the resending of verification email if user does not recieve it.
  const resendEmailVerification = (): void => {
    try {
      if (sign_in_user && verify_timeout === 0) {
        sendEmailVerification(sign_in_user).catch((err) => {
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              err,
              {
                sign_in_user: sign_in_user,
                verify_timeout: verify_timeout
              },
              tracking_id
            )
          );
          setFeedbackData({
            message: `${err.message}. Tracking ID: ${tracking_id}`,
            state: true,
            type: 'error'
          });
        });
        resetVerifyTimeout();
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: VerifyEmail failed on an unknown error while calling resendEmailVerification.',
          {
            sign_in_user: sign_in_user,
            verify_timeout: verify_timeout
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to resend email verification. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <MailOutlineIcon sx={{ fontSize: '1.7rem' }} />
        <DialogTitle>Check your Inbox</DialogTitle>
      </Box>
      <DialogContentText sx={{ my: 4 }}>
        Account Verification link sent to Email Address. Sign in again after clicking the link to
        confirm your Account.
      </DialogContentText>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 6, gap: 2 }}>
        <PrimaryButton
          disabled={verify_timeout > 0}
          id="resend-button"
          onClick={() => resendEmailVerification()}
        >
          Resend Email {verify_timeout > 0 ? verify_timeout : ''}
        </PrimaryButton>
        <GhostButton outlined dark id="back-button" onClick={() => returnToSignIn()}>
          Return to Sign In
        </GhostButton>
      </Box>

      <Typography sx={{ textAlign: 'center', color: '#053d5d', my: 2, fontSize: '0.9rem' }}>
        Contact us if you have any questions
      </Typography>
    </Box>
  );
};

export default VerifyEmail;
